import { v4 as uuidv4 } from 'uuid';
import { html } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { FormApi } from 'final-form';

export interface Output {
  element: Element;
  id: string;
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function findParentWithClass(target: HTMLElement | null, className: string): HTMLElement | null {
  while (target !== null && !target.classList.contains(className)) {
    target = target.parentElement;
  }
  return target;
}

// Climbs from element out through each shadow root to the app's root element, <ns-app>, adding a
// slot at each shadow host so content appended to the root shows where element is. Returns null if
// element isn't inside <ns-app>.
export function findRootAppElement(element: Element, slotId: string, persistSlotId: string | undefined): Output | null {
  if (element.localName === 'ns-app' || element.getAttribute('scope') === 'ROOT') {
    return {
      element: element,
      id: slotId,
    };
  }

  slotId = slotId ? slotId : uuidv4();
  const outerSlotId = element.shadowRoot && persistSlotId ? persistSlotId : element.shadowRoot ? uuidv4() : slotId;
  if (element.shadowRoot) {
    const slotPlaceholder = document.createElement('div');
    slotPlaceholder.setAttribute('style', 'height: 100%;');
    slotPlaceholder.slot = slotId.includes('stripe') ? slotId : `slot-${slotId}`;

    const slot = document.createElement('slot');
    slot.name = `slot-${outerSlotId}`;

    slotPlaceholder.appendChild(slot);
    element.appendChild(slotPlaceholder);
  }

  let parentNode = element.parentNode as Element;
  if (parentNode instanceof ShadowRoot) {
    parentNode = parentNode.host as Element;
  }

  // Above <html> is the document, which isn't an element
  if (parentNode instanceof Element) {
    return findRootAppElement(parentNode, outerSlotId, persistSlotId);
  }

  return null;
}

export function limitInput(ev: CustomEvent, min: string, max: string) {
  const target = ev.target as HTMLInputElement;
  if (!target) throw new Error('Missing required target');

  if (target.value.length > max.length) {
    target.value = target.value.slice(0, max.length);
  }

  const value = target.value;
  if (value && Number(value) && Number(value) < Number(min)) {
    target.value = min;
  }
  if (value && Number(value) && Number(value) > Number(max)) {
    target.value = max;
  }

  return target.value;
}

export function getDeepValue(obj: any, path: string) {
  if (!obj || !path) return undefined;

  return path.split('.').reduce((acc, part) => {
    const value = acc?.[part];
    return Array.isArray(value) ? value[0] : value;
  }, obj);
}

export function renderFormErrorSpan(field: any, errors: Partial<Record<keyof any, string>>, form?: FormApi<any>) {
  const hasValue = form ? !!form.getFieldState(field)?.value : false;
  return html`
    <span
      class="form-error medium"
      role="alert"
      style=${styleMap({ display: errors[field] && !hasValue ? 'block' : 'none' })}
    >
      ${errors[field]}
    </span>
  `;
}

export function capitalizeWords(sentence: string) {
  if (!sentence) return '';

  return sentence
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
